import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import { api, trackEvent } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import InstitutionalCta from "../components/InstitutionalCta";
import { Button } from "../components/ui/button";

const ORDER = ["free", "report_single", "pro", "business", "institutional", "enterprise"];
const FEATURES = {
  free: 4, report_single: 4, pro: 5, business: 6, institutional: 4, enterprise: 4,
};

const fmt = (g) => (g == null ? null : (g / 100).toLocaleString("pl-PL", { maximumFractionDigits: 0 }));

export default function PricingPage() {
  const { t, lang, toggle } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);

  // /en/pricing forces English on first paint (single EN alias route in the app).
  useEffect(() => {
    if (location.pathname.startsWith("/en/") && lang !== "en") toggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackEvent("pricing_view");
    api.get("/billing/plans").then((r) => {
      setPlans(r.data.plans);
      setPaymentsEnabled(r.data.payments_enabled !== false);
    }).catch(() => setPlans([]));
  }, []);

  const goCheckout = (code) => {
    if (!paymentsEnabled) return;
    const target = `/platnosc/plan/${code}`;
    if (!user) {
      navigate(`/logowanie?returnTo=${encodeURIComponent(target)}`);
      return;
    }
    navigate(target);
  };

  const byCode = Object.fromEntries((plans || []).map((p) => [p.code, p]));

  return (
    <div data-testid="pricing-page" className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <Helmet>
        <title>{`${t("pricing.title")} — PlanGmina`}</title>
        <meta name="description" content={t("pricing.subtitle")} />
      </Helmet>
      <header className="max-w-2xl">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {t("pricing.title")}
        </h1>
        <p className="mt-2 text-base text-slate-600">{t("pricing.subtitle")}</p>
        <p data-testid="pricing-tax-note" className="mt-3 text-xs text-slate-400">{t("pricing.tax_note")}</p>
        <p data-testid="pricing-test-note" className="mt-1 text-xs font-semibold text-amber-700">
          {paymentsEnabled ? t("pricing.test_note") : t("pricing.payments_off_note")}
        </p>
      </header>

      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {ORDER.map((code) => {
          const p = byCode[code];
          if (!p) return null;
          const price = p.price_grosze == null
            ? (lang === "pl" ? p.price_display_pl : p.price_display_en)
            : p.price_grosze === 0
              ? "0 zł"
              : p.price_display_pl && lang === "pl" ? p.price_display_pl
                : p.price_display_en && lang === "en" ? p.price_display_en
                  : `${fmt(p.price_grosze)} zł ${t(`pricing.interval.${p.billing_interval}`)}`;
          return (
            <div
              key={code}
              data-testid={`pricing-card-${code}`}
              className={`flex flex-col rounded-2xl border bg-white p-6 ${
                code === "pro" ? "border-[#DC143C] shadow-lg ring-1 ring-[#DC143C]" : "border-slate-200"
              }`}
            >
              {code === "pro" && (
                <span className="mb-2 w-fit rounded-full bg-[#DC143C] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  {t("pricing.most_popular")}
                </span>
              )}
              <h2 className="font-heading text-lg font-bold text-slate-900">
                {lang === "pl" ? p.name_pl : p.name_en}
              </h2>
              <p data-testid={`pricing-price-${code}`} className="mt-1 font-heading text-2xl font-extrabold text-slate-900">
                {price}
              </p>
              <ul className="mt-4 flex-1 space-y-2">
                {Array.from({ length: FEATURES[code] }, (_, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check size={15} className="mt-0.5 shrink-0 text-emerald-600" />
                    {t(`pricing.f.${code}.${i + 1}`)}
                  </li>
                ))}
              </ul>
              <div className="mt-5">
                {code === "free" && (
                  <Button data-testid="pricing-cta-free" variant="outline" className="w-full" onClick={() => navigate("/")}>
                    {t("pricing.cta.free")}
                  </Button>
                )}
                {code === "report_single" && (
                  <Button data-testid="pricing-cta-report" variant="outline" className="w-full" onClick={() => navigate("/")}>
                    {t("pricing.cta.report_single")}
                  </Button>
                )}
                {(code === "pro" || code === "business") && (
                  <Button
                    data-testid={`pricing-cta-${code}`}
                    disabled={!paymentsEnabled}
                    className={`w-full ${code === "pro" ? "bg-[#DC143C] text-white hover:bg-[#B91C1C]" : "bg-slate-900 text-white hover:bg-slate-800"} disabled:opacity-60`}
                    onClick={() => goCheckout(code)}
                  >
                    {paymentsEnabled ? t(`pricing.cta.${code}`) : t("pricing.cta.soon")}
                  </Button>
                )}
                {p.cta_only && code !== "institutional_pilot" && (
                  <Button
                    data-testid={`pricing-cta-${code}`}
                    variant="outline"
                    className="w-full"
                    onClick={() => document.getElementById("instytucjonalny")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    {t(`pricing.cta.${code}`)}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <section id="instytucjonalny" data-testid="pricing-institutional" className="mt-12">
        <InstitutionalCta variant="card" />
      </section>
    </div>
  );
}
